'use client'
import { XIcon, ArrowLeftIcon } from '@phosphor-icons/react'
import { type JSX, memo, useState } from 'react'
import { useClearRefinements } from 'react-instantsearch'

import { Button } from '@/components/primitives/Button/Button'
import { Drawer } from '@/components/primitives/Drawer/Drawer'
import { cn } from '@/lib/utils'

import { FacetItem } from './FacetItem'
import { FacetOptions } from './FacetOptions'

export type Facet = { value: string | null; label: string | null }

export const FACET_LABELS = {
  product_type: 'Category',
  'options.cylinder_size': 'Cylinder Size',
  'options.cylinder_contents_(kg)': 'Cylinder Content (kg)',
  'named_tags.cylinder_content_m3': 'Cylinder Content (m3)',
  'options.tank_size_(l)': 'Tank Size',
  'options.colour': 'Colour',
  'options.model': 'Model',
}
type FacetType = keyof typeof FACET_LABELS

export const FACETS = Object.keys(FACET_LABELS) as FacetType[]

export const CollectionFiltersDrawer = memo(function CollectionFiltersDrawer({ children }: { children: JSX.Element }) {
  const [currentFacet, setCurrentFacet] = useState<FacetType | null>(null)
  const [showOptions, setShowOptions] = useState(false)
  const { refine } = useClearRefinements({
    includedAttributes: FACETS,
  })
  const resetDrawer = () => {
    setShowOptions(false)
    setCurrentFacet(null)
  }

  return (
    <Drawer direction="right" aria-label="Filters" onOpenChange={(isOpen) => !isOpen && setTimeout(() => setCurrentFacet(null), 300)}>
      <Drawer.Trigger>{children}</Drawer.Trigger>
      <Drawer.Content className={'no-scrollbar relative bottom-0 left-0 h-full overflow-y-auto overflow-x-hidden rounded-l-xl bg-grey-100'}>
        <div className={cn('flex h-full flex-1 flex-col justify-between duration-200')}>
          <Drawer.Header className="relative bg-white px-6 py-5">
            <div className={cn('flex items-center justify-between')}>
              {showOptions ? (
                <button
                  title="Back"
                  onClick={() => {
                    setShowOptions(false)
                  }}
                >
                  <ArrowLeftIcon size={24} />
                </button>
              ) : (
                <div />
              )}
              <h3 className="text-lg font-semibold capitalize">{showOptions && currentFacet ? FACET_LABELS[currentFacet] : 'Filters'}</h3>
              <Drawer.Close>
                <XIcon
                  size={24}
                  className="cursor-pointer text-grey-500"
                  onClick={() => {
                    resetDrawer()
                  }}
                />
              </Drawer.Close>
            </div>
          </Drawer.Header>

          <Drawer.Body className="relative mt-2 flex flex-1 flex-col justify-between overflow-y-auto bg-white p-6 pt-0">
            <ul className="flex flex-col">
              {FACETS?.map((facet) => (
                <FacetItem
                  key={facet}
                  facet={facet}
                  onClick={() => {
                    setCurrentFacet(facet)
                    setShowOptions(true)
                  }}
                />
              ))}
            </ul>
            {currentFacet && <FacetOptions facet={currentFacet} show={showOptions} />}
          </Drawer.Body>
          <Drawer.Footer className="flex gap-4 border-t border-t-grey-200 bg-grey-100 px-6 py-5 ">
            <Drawer.Close>
              <Button variant="solid" className="w-full border-[1px] border-white bg-primary-600 text-white hover:bg-primary-700">
                Show Results
              </Button>
            </Drawer.Close>
            <Drawer.Close>
              <Button variant="solid" className="w-full border-[1px] border-white bg-primary-600 text-white hover:bg-primary-700" onClick={() => refine()}>
                Clear
              </Button>
            </Drawer.Close>
          </Drawer.Footer>
        </div>
      </Drawer.Content>
    </Drawer>
  )
})
